<template>
  <div class="fa-fleet-view">
    <!-- KPI strip -->
    <div class="fa-kpi-strip">
      <div class="fa-kpi">
        <div class="fa-kpi-label">Tenants w/ FA</div>
        <div class="fa-kpi-value">{{ kpis.enabled_count }}<span class="fa-kpi-sub"> / {{ kpis.total_count }}</span></div>
      </div>
      <div class="fa-kpi">
        <div class="fa-kpi-label">Active users (this month)</div>
        <div class="fa-kpi-value">{{ kpis.active_users }}</div>
      </div>
      <div class="fa-kpi">
        <div class="fa-kpi-label">Blended cost</div>
        <div class="fa-kpi-value">{{ formatCents(kpis.total_cost_cents) }}</div>
      </div>
      <div class="fa-kpi">
        <div class="fa-kpi-label">Requests</div>
        <div class="fa-kpi-value">{{ kpis.chat_requests }}<span class="fa-kpi-sub"> chat · </span>{{ kpis.doc_categorizations }}<span class="fa-kpi-sub"> docs</span></div>
      </div>
      <div class="fa-kpi fa-kpi--alert" v-if="kpis.over_quota_count > 0">
        <div class="fa-kpi-label">Over quota</div>
        <div class="fa-kpi-value">{{ kpis.over_quota_count }}</div>
      </div>
    </div>

    <!-- toolbar — "Loaded X ago" stays as the at-a-glance freshness
         signal; the loading-progress indicator was redundant with the
         spin animation on the Refresh button's icon (which already
         fires while `loading === true`) so it was just toolbar noise. -->
    <div class="fa-toolbar">
      <div style="flex: 1;">
        <span v-if="loadedAt" style="font-size: 12px; color: #888;">Loaded {{ loadedAtLabel }}</span>
      </div>
      <b-button size="sm" variant="outline-primary" :disabled="loading" @click="$emit('refresh')">
        <i class="fa fa-refresh" :class="{ 'fa-spin': loading }" style="margin-right: 4px;"></i>Refresh
      </b-button>
    </div>

    <!-- reconciliation panel — collapsed by default; click header to expand -->
    <div v-if="reconciledRows.length > 0" class="fa-recon-panel">
      <button type="button"
              class="fa-recon-header"
              :aria-expanded="reconExpanded ? 'true' : 'false'"
              @click="reconExpanded = !reconExpanded">
        <i class="fa fa-balance-scale" style="margin-right: 6px;"></i>
        Reconciled this month ({{ reconciledRows.length }})
        <i class="fa fa-chevron-down fa-recon-chevron"
           :class="{ 'fa-recon-chevron--open': reconExpanded }"
           aria-hidden="true"></i>
      </button>
      <b-collapse id="fa-recon-collapse" v-model="reconExpanded">
        <div class="fa-recon-rows">
          <div v-for="r in reconciledRows" :key="'recon-' + r.tenant_id" class="fa-recon-row">
            <span class="fa-recon-tenant">{{ r.tenant_name }}</span>
            <span class="fa-recon-delta">
              est {{ formatCents(r.estimated_cost_cents_at_reconcile) }} → actual {{ formatCents(r.exact_cost_cents) }}
              <span :class="reconDeltaClass(r)">({{ reconDeltaLabel(r) }})</span>
            </span>
          </div>
        </div>
      </b-collapse>
    </div>

    <!-- skeleton state — initial load only. once `rows` has data, future
         Refresh clicks keep showing the stale rows in place (refresh
         icon spins to signal in-flight) so refreshes don't trigger a
         full layout flush. mirrors the announcement_list pattern. -->
    <div v-if="loading && rows.length === 0" class="table-responsive fa-fleet-table">
      <table class="table b-table table-striped table-sm fa-fleet-table--skeleton" aria-hidden="true">
        <thead>
          <tr>
            <th v-for="f in fields" :key="'fa-skel-h-' + f.key" :style="f.thStyle">{{ f.label }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="i in 8" :key="'fa-skel-row-' + i">
            <td><span class="fa-skel fa-skel-pill"></span></td>
            <td><span class="fa-skel fa-skel-text" :style="{ width: skeletonTenantWidth(i) }"></span></td>
            <td><span class="fa-skel fa-skel-text" style="width: 24px"></span></td>
            <td><span class="fa-skel fa-skel-text" style="width: 24px"></span></td>
            <td><span class="fa-skel fa-skel-text" style="width: 44px"></span></td>
            <td><span class="fa-skel fa-skel-bar"></span></td>
            <td><span class="fa-skel fa-skel-text" style="width: 70px"></span></td>
            <td><span class="fa-skel fa-skel-text" style="width: 60px"></span></td>
            <td><span class="fa-skel fa-skel-btn"></span></td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- tenant table -->
    <b-table
      v-else
      :items="rows"
      :fields="fields"
      small
      striped
      hover
      responsive
      sort-icon-left
      sort-by="last_activity"
      :sort-desc="true"
      class="fa-fleet-table">

      <template slot="tenant_name" slot-scope="row">
        <a href="#" @click.prevent="$emit('open-tenant', row.item.tenant_id)" class="fa-tenant-link">{{ row.item.tenant_name }}</a>
      </template>

      <template slot="fa_enabled" slot-scope="row">
        <!-- icon-only pill mirroring support_ticket_list's priority-tag pattern
             so status reads at a glance and the column stays narrow. tooltip
             carries the label for muggles / unfamiliar readers. -->
        <span class="fa-status-pill"
              :class="row.item.fa_enabled ? 'fa-status-pill--on' : 'fa-status-pill--off'"
              :title="row.item.fa_enabled ? 'Fleet Assistant enabled' : 'Fleet Assistant disabled'"
              :aria-label="row.item.fa_enabled ? 'Fleet Assistant enabled' : 'Fleet Assistant disabled'">
          <i class="fa" :class="row.item.fa_enabled ? 'fa-check-circle' : 'fa-times-circle'" aria-hidden="true"></i>
        </span>
      </template>

      <template slot="blended_cost_cents" slot-scope="row">
        {{ formatCents(row.item.blended_cost_cents) }}
      </template>

      <template slot="quota_pct" slot-scope="row">
        <div class="fa-quota-cell">
          <div class="fa-quota-bar">
            <div class="fa-quota-fill" :class="quotaFillClass(row.item)" :style="{ width: clampPct(row.item.quota_pct) + '%' }"></div>
          </div>
          <span class="fa-quota-label">{{ row.item.quota_pct }}%</span>
        </div>
      </template>

      <template slot="projection" slot-scope="row">
        <span v-if="row.item.projected_limit_hit" style="color: #b58100; font-size: 12px;">
          <i class="fa fa-exclamation-triangle"/> hits {{ row.item.projected_limit_hit }}
        </span>
        <span v-else style="color: #888; font-size: 12px;">—</span>
      </template>

      <template slot="anomalies" slot-scope="row">
        <span v-for="flag in row.item.anomaly_flags" :key="flag.key"
              class="fa-anomaly-chip" :class="'fa-anomaly-chip--' + flag.severity"
              :title="flag.label">{{ flag.label }}</span>
        <span v-if="!row.item.anomaly_flags || row.item.anomaly_flags.length === 0" style="color: #bbb;">—</span>
      </template>

      <template slot="last_activity" slot-scope="row">
        <span style="font-size: 12px; color: #555;"
              :title="row.item.last_activity ? formatEpoch(row.item.last_activity) : ''">{{ formatEpochCompact(row.item.last_activity) }}</span>
      </template>

      <template slot="actions" slot-scope="row">
        <b-button size="sm" variant="outline-primary" @click="$emit('open-tenant', row.item.tenant_id)">View</b-button>
      </template>
    </b-table>
  </div>
</template>

<script>
import { formatCents, formatEpoch, formatEpochCompact } from '../../helpers/fa_admin'

export default {
  name: 'FaFleetView',
  props: {
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    loadedAt: { type: Number, default: 0 },
    loadedRows: { type: Number, default: 0 },
    totalTenants: { type: Number, default: 0 },
  },
  data () {
    return {
      reconExpanded: false,
    }
  },
  computed: {
    loadedAtLabel () {
      if (!this.loadedAt) return ''
      const delta = Math.max(0, Math.floor((Date.now() - this.loadedAt) / 1000))
      if (delta < 60) return delta + 's ago'
      if (delta < 3600) return Math.floor(delta / 60) + 'm ago'
      return Math.floor(delta / 3600) + 'h ago'
    },
    kpis () {
      const total = this.rows.length
      const enabled = this.rows.filter(r => r.fa_enabled).length
      const activeUsers = this.rows.reduce((s, r) => s + (r.active_users || 0), 0)
      const cost = this.rows.reduce((s, r) => s + (r.blended_cost_cents || 0), 0)
      const chat = this.rows.reduce((s, r) => s + (r.chat_requests || 0), 0)
      const docs = this.rows.reduce((s, r) => s + (r.doc_categorizations || 0), 0)
      const over = this.rows.filter(r => (r.quota_pct || 0) >= 100).length
      return {
        total_count: total,
        enabled_count: enabled,
        active_users: activeUsers,
        total_cost_cents: cost,
        chat_requests: chat,
        doc_categorizations: docs,
        over_quota_count: over,
      }
    },
    reconciledRows () {
      return this.rows.filter(r => r.reconciled_at && r.reconciled_at > 0)
    },
    fields () {
      // FA enabled-status leads — at-a-glance signal for "is this tenant
      // actually using the assistant?" before any other column. tenant
      // name follows so the link target sits next to the status.
      return [
        { key: 'fa_enabled', label: 'FA', sortable: true, thStyle: { width: '60px' } },
        { key: 'tenant_name', label: 'Tenant', sortable: true },
        // Users column dropped from the fleet view — the bulk fan-out
        // hardcodes active_users to 0 (per-tenant distinct-user counts
        // are too expensive at fleet scope). real per-user counts live
        // on the tenant drill-down's "Users this month" table.
        { key: 'chat_requests', label: 'Chats', sortable: true, thStyle: { width: '70px' } },
        { key: 'doc_categorizations', label: 'Docs', sortable: true, thStyle: { width: '70px' } },
        { key: 'blended_cost_cents', label: 'Cost', sortable: true, thStyle: { width: '90px' } },
        { key: 'quota_pct', label: '% quota', sortable: true, thStyle: { width: '140px' } },
        { key: 'projection', label: 'Projection', sortable: false, thStyle: { width: '150px' } },
        { key: 'last_activity', label: 'Last activity', sortable: true, thStyle: { width: '140px' } },
        { key: 'actions', label: '', sortable: false, thStyle: { width: '70px' } },
      ]
    },
  },
  methods: {
    formatCents,
    formatEpoch,
    formatEpochCompact,
    clampPct (v) { return Math.max(0, Math.min(100, Number(v) || 0)) },
    quotaFillClass (row) {
      const pct = row.quota_pct || 0
      if (pct >= 100) return 'fa-quota-fill--danger'
      if (pct >= 80) return 'fa-quota-fill--warning'
      return 'fa-quota-fill--ok'
    },
    reconDeltaClass (r) {
      const est = Number(r.estimated_cost_cents_at_reconcile || 0)
      const actual = Number(r.exact_cost_cents || 0)
      if (actual > est * 1.2) return 'fa-recon-neg'
      if (actual < est * 0.8) return 'fa-recon-pos'
      return 'fa-recon-neutral'
    },
    reconDeltaLabel (r) {
      const est = Number(r.estimated_cost_cents_at_reconcile || 0)
      const actual = Number(r.exact_cost_cents || 0)
      if (est <= 0) return '—'
      const diff = actual - est
      const pct = Math.round((diff / est) * 100)
      const sign = diff >= 0 ? '+' : ''
      return sign + pct + '% vs estimate'
    },
    // vary the tenant-name placeholder width per skeleton row so the
    // 8-row block doesn't read as 8 identical stripes. mirrors the
    // support_ticket_list mobile-card skeleton's "every-other-row gets
    // a different width" approach.
    skeletonTenantWidth (i) {
      const widths = ['72%', '54%', '88%', '60%', '78%', '46%', '70%', '64%']
      return widths[(i - 1) % widths.length]
    },
  },
}
</script>

<style scoped>
.fa-fleet-view { padding: 8px 0; }

.fa-kpi-strip {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.fa-kpi {
  flex: 1 1 160px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px 14px;
  min-width: 160px;
}
.fa-kpi--alert {
  background: #fff2ea;
  border-color: #f0a957;
}
.fa-kpi-label {
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}
.fa-kpi-value {
  font-size: 20px;
  font-weight: bold;
  color: #333;
}
.fa-kpi-sub {
  font-size: 12px;
  font-weight: normal;
  color: #888;
}

.fa-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.fa-recon-panel {
  background: #fff8ef;
  border: 1px solid #f0d5a8;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 14px;
}
.fa-recon-header {
  font-weight: bold;
  color: #8a6100;
  font-size: 13px;
  margin-bottom: 6px;
  background: transparent;
  border: 0;
  padding: 0;
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
  cursor: pointer;
}
.fa-recon-header:hover { color: #6b4a00; }
.fa-recon-chevron {
  margin-left: 6px;
  font-size: 11px;
  color: #b58100;
  transition: transform 0.15s ease;
}
.fa-recon-chevron--open { transform: rotate(180deg); }
.fa-recon-rows {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.fa-recon-row {
  font-size: 12px;
  display: flex;
  gap: 8px;
  align-items: center;
}
.fa-recon-tenant {
  font-weight: 600;
  color: #333;
  min-width: 180px;
}
.fa-recon-delta { color: #555; }
.fa-recon-pos { color: #28a745; font-weight: 600; }
.fa-recon-neg { color: #dc3545; font-weight: 600; }
.fa-recon-neutral { color: #888; }

.fa-tenant-link {
  color: #007ac7;
  font-weight: 600;
  text-decoration: none;
}
.fa-tenant-link:hover { text-decoration: underline; }

.fa-quota-cell {
  display: flex;
  align-items: center;
}
.fa-quota-bar {
  flex: 1;
  height: 8px;
  background: #eee;
  border-radius: 4px;
  overflow: hidden;
  min-width: 70px;
}
.fa-quota-fill {
  height: 100%;
  transition: width 0.3s;
}
.fa-quota-fill--ok { background: #28a745; }
.fa-quota-fill--warning { background: #f0a957; }
.fa-quota-fill--danger { background: #dc3545; }
.fa-quota-label {
  font-size: 11px;
  color: #555;
  min-width: 34px;
  text-align: right;
}

.fa-anomaly-chip {
  display: inline-block;
  font-size: 11px;
  padding: 1px 8px;
  border-radius: 10px;
  margin-right: 4px;
  margin-bottom: 2px;
}
.fa-anomaly-chip--danger {
  background: #fde4e4;
  color: #a02020;
  border: 1px solid #f5b5b5;
}
.fa-anomaly-chip--warning {
  background: #fff2ea;
  color: #8a6100;
  border: 1px solid #f0d5a8;
}
.fa-anomaly-chip--muted {
  background: #f2f2f2;
  color: #666;
  border: 1px solid #ddd;
}

/* icon-only status pill mirroring the priority-tag pattern in
   support_ticket_list.vue — same geometry, same pastel fill + border
   family, same cursor-help affordance. kept narrow so the FA column
   stays scannable. */
.fa-status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 5px;
  border-radius: 4px;
  border: 1px solid;
  width: 26px;
  height: 22px;
  cursor: help;
  box-sizing: border-box;
}
.fa-status-pill .fa {
  font-size: 13px;
  line-height: 1;
}
.fa-status-pill--on {
  background: #edf7ee;
  border-color: #3a8144;
  color: #1f5727;
}
.fa-status-pill--off {
  background: #f2f2f2;
  border-color: #c4c4c4;
  color: #888;
}

/* canonical b-table treatment from support_ticket_list (see
   standard_table_pattern.md): 13px body / 12px header, dense 3px 8px
   padding, horizontal separators only, uppercase muted sticky header,
   steelblue sort arrows, soft #f0f5ff hover row. b-table's `responsive`
   prop wraps the table in `.table-responsive` (overflow-x: auto) so on
   narrow viewports the table scrolls horizontally rather than reflowing.
   FA Insights tables are dense enough that horizontal scroll reads
   better than the support_ticket_list's mobile card collapse — there's
   no obvious "card form" of an 11-column dashboard.
   `>>>` deep selectors needed because BV2 renders `.table.b-table` /
   `thead th` etc. internally and they don't pick up the scoped-CSS
   data-v-HASH attribute on their own. */
.fa-fleet-view >>> .table.b-table {
  font-size: 13px;
  border-collapse: collapse;
  margin-bottom: 0;
}
.fa-fleet-view >>> .table.b-table th,
.fa-fleet-view >>> .table.b-table td {
  border-left: none !important;
  border-right: none !important;
  border-bottom: 1px solid #e0e0e0;
  padding: 3px 8px !important;
  vertical-align: middle !important;
  text-align: left !important;
}
.fa-fleet-view >>> .table.b-table th:first-child,
.fa-fleet-view >>> .table.b-table td:first-child { padding-left: 12px !important; }
.fa-fleet-view >>> .table.b-table th:last-child,
.fa-fleet-view >>> .table.b-table td:last-child { padding-right: 12px !important; }
.fa-fleet-view >>> .table.b-table thead th {
  background: #f8f9fa;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #555;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 2;
  /* sticky positioning kills the bottom border; replace it with an
     inset shadow so the header still has its hairline divider. */
  box-shadow: inset 0 -2px 0 0 #ccc;
}
.fa-fleet-view >>> .table.b-table tbody tr:hover td { background-color: #f0f5ff; }
/* sort arrows — override BV's floated defaults so the indicator sits
   compactly to the right of the label and turns steelblue when the
   column is the active sort. */
.fa-fleet-view >>> th[aria-sort] {
  background-image: none !important;
  padding-right: 0.75rem !important;
  white-space: nowrap;
}
.fa-fleet-view >>> th[aria-sort]::before { display: none !important; }
.fa-fleet-view >>> th[aria-sort]::after {
  display: inline-block !important;
  margin-left: 4px;
  font-size: 10px;
  color: #999;
  vertical-align: middle;
  float: none !important;
  width: auto !important;
  height: auto !important;
  opacity: 1 !important;
}
.fa-fleet-view >>> th[aria-sort="none"]::after { content: "\2195" !important; }
.fa-fleet-view >>> th[aria-sort="ascending"]::after { content: "\25B2" !important; color: steelblue; }
.fa-fleet-view >>> th[aria-sort="descending"]::after { content: "\25BC" !important; color: steelblue; }

/* skeleton rows — shown only on the initial load (no rows yet). same
   table classes as the b-table so the canonical .table.b-table styling
   above applies identically (uppercase muted header, dense padding,
   horizontal separators). placeholder bars shimmer left-to-right via
   the same gradient + keyframes used elsewhere in the app. */
.fa-fleet-table--skeleton { table-layout: fixed; }
.fa-fleet-table--skeleton tbody td { vertical-align: middle; }
.fa-skel {
  display: inline-block;
  background: linear-gradient(90deg, #ececec 0%, #f5f5f5 50%, #ececec 100%);
  background-size: 200% 100%;
  border-radius: 4px;
  animation: fa-fleet-skel 1.4s ease-in-out infinite;
  line-height: 1;
}
.fa-skel-text { display: inline-block; height: 12px; }
.fa-skel-pill { width: 22px; height: 18px; border-radius: 4px; }
.fa-skel-bar { display: block; width: 92%; height: 8px; border-radius: 4px; }
.fa-skel-btn { display: inline-block; width: 46px; height: 24px; border-radius: 4px; }
@keyframes fa-fleet-skel {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
